import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Plus,
  Search,
  Building2,
  Calendar,
  MapPin,
  MoreVertical,
  ChevronRight,
  Users,
  X
} from "lucide-react";
import { toast } from "sonner";

export const ProjectsPage = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    project_number: "",
    address: "",
    start_date: "",
    end_date: "",
    budget: 0
  });

  useEffect(() => {
    fetchProjects();
    fetchOrgUsers();
  }, []);

  const fetchOrgUsers = async () => {
    try {
      const res = await fetch(`${API}/available-users`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      if (res.ok) setOrgUsers(await res.json());
    } catch (err) { console.error("Failed to fetch users:", err); }
  };

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
      }
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, members: selectedMembers.map(m => ({ user_id: m.user_id, role: m.role })) };
      const res = await fetch(`${API}/projects`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const memberCount = selectedMembers.length;
        toast.success(language === "sv" 
          ? `Projekt skapat!${memberCount > 0 ? ` ${memberCount} medarbetare tillagda.` : ""}`
          : `Project created!${memberCount > 0 ? ` ${memberCount} members added.` : ""}`);
        setDialogOpen(false);
        setFormData({ name: "", description: "", project_number: "", address: "", start_date: "", end_date: "", budget: 0 });
        setSelectedMembers([]);
        setMemberSearch("");
        fetchProjects();
      } else {
        toast.error(language === "sv" ? "Kunde inte skapa projekt" : "Failed to create project");
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const addMember = (u, role = "worker") => {
    if (selectedMembers.some(m => m.user_id === u.user_id)) return;
    setSelectedMembers(prev => [...prev, { user_id: u.user_id, name: u.name, email: u.email, role }]);
    setMemberSearch("");
  };

  const removeMember = (userId) => {
    setSelectedMembers(prev => prev.filter(m => m.user_id !== userId));
  };

  const updateMemberRole = (userId, role) => {
    setSelectedMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role } : m));
  };

  const availableUsers = orgUsers.filter(u => 
    u.user_id !== user?.user_id &&
    !selectedMembers.some(m => m.user_id === u.user_id) &&
    (u.name?.toLowerCase().includes(memberSearch.toLowerCase()) || u.email?.toLowerCase().includes(memberSearch.toLowerCase()))
  );

  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.project_number?.toLowerCase().includes(search.toLowerCase())
  );

  const statusColors = {
    active: "bg-green-100 text-green-800",
    completed: "bg-blue-100 text-blue-800",
    on_hold: "bg-amber-100 text-amber-800",
    cancelled: "bg-red-100 text-red-800"
  };

  const statusLabels = {
    active: language === "sv" ? "Aktiv" : "Active",
    completed: language === "sv" ? "Avslutad" : "Completed",
    on_hold: language === "sv" ? "Pausad" : "On Hold",
    cancelled: language === "sv" ? "Avbruten" : "Cancelled"
  };

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="projects-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" ? "Projekt" : "Projects"}
            </h1>
            <p className="text-slate-600 mt-1">
              {language === "sv" 
                ? `${projects.length} projekt totalt` 
                : `${projects.length} projects total`}
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-touch" data-testid="create-project-btn">
                <Plus className="w-5 h-5 mr-2" />
                {language === "sv" ? "Nytt projekt" : "New project"}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                  {language === "sv" ? "Skapa nytt projekt" : "Create new project"}
                </DialogTitle>
                <DialogDescription className="text-sm text-slate-500">
                  {language === "sv" ? "Fyll i projektinformation och lägg till medarbetare" : "Fill in project details and add team members"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Projektnamn" : "Project name"} *</Label>
                  <Input 
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder={language === "sv" ? "T.ex. Kv. Björken" : "E.g. Oak Building"}
                    required
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Projektnummer" : "Project number"}</Label>
                  <Input 
                    value={formData.project_number}
                    onChange={(e) => setFormData({...formData, project_number: e.target.value})}
                    placeholder="2024-001"
                    data-testid="project-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Beskrivning" : "Description"}</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder={language === "sv" ? "Kort beskrivning av projektet..." : "Brief project description..."}
                    rows={3}
                    data-testid="project-desc-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Adress" : "Address"}</Label>
                  <Input 
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder={language === "sv" ? "Storgatan 1, Stockholm" : "123 Main St, Stockholm"}
                    data-testid="project-address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Startdatum" : "Start date"}</Label>
                    <Input 
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      data-testid="project-start-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Slutdatum" : "End date"}</Label>
                    <Input 
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      data-testid="project-end-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Budget (SEK)" : "Budget (SEK)"}</Label>
                  <Input 
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData({...formData, budget: parseFloat(e.target.value) || 0})}
                    placeholder="0"
                    data-testid="project-budget-input"
                  />
                </div>

                {/* Members Section */}
                <div className="space-y-2">
                  <Label className="form-label flex items-center gap-1.5">
                    <Users className="w-4 h-4" />
                    {language === "sv" ? "Medarbetare" : "Members"}
                    {selectedMembers.length > 0 && (
                      <Badge className="bg-blue-100 text-blue-700 ml-1">{selectedMembers.length}</Badge>
                    )}
                  </Label>

                  {/* Selected members */}
                  {selectedMembers.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2" data-testid="selected-members">
                      {selectedMembers.map(m => (
                        <div key={m.user_id} className="flex items-center gap-1.5 bg-slate-100 rounded-full pl-1 pr-2 py-1" data-testid={`selected-member-${m.user_id}`}>
                          <div className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center text-xs font-medium text-blue-800">
                            {m.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <span className="text-sm text-slate-700">{m.name}</span>
                          <select
                            value={m.role}
                            onChange={(e) => updateMemberRole(m.user_id, e.target.value)}
                            className="text-xs bg-transparent border-none text-slate-500 cursor-pointer py-0 pl-0 pr-1 focus:ring-0"
                            data-testid={`member-role-${m.user_id}`}
                          >
                            <option value="worker">{language === "sv" ? "Medarbetare" : "Worker"}</option>
                            <option value="project_manager">{language === "sv" ? "Projektledare" : "Project Manager"}</option>
                            <option value="site_manager">{language === "sv" ? "Platschef" : "Site Manager"}</option>
                          </select>
                          <button type="button" onClick={() => removeMember(m.user_id)} className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`remove-member-${m.user_id}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Member search */}
                  <div className="relative">
                    <Input
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      placeholder={language === "sv" ? "Sök medarbetare att lägga till..." : "Search members to add..."}
                      className="h-9 text-sm"
                      data-testid="member-search-input"
                    />
                    {memberSearch.length > 0 && availableUsers.length > 0 && (
                      <div className="absolute z-50 top-full mt-1 left-0 w-full bg-white rounded-lg shadow-lg border border-slate-200 max-h-[150px] overflow-y-auto" data-testid="member-search-dropdown">
                        {availableUsers.slice(0, 5).map(u => (
                          <button
                            key={u.user_id}
                            type="button"
                            onClick={() => addMember(u)}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                            data-testid={`member-option-${u.user_id}`}
                          >
                            <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600">
                              {u.name?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-slate-800 block truncate">{u.name}</span>
                              <span className="text-xs text-slate-400 block truncate">{u.email}</span>
                            </div>
                            <span className="text-xs text-slate-400 capitalize">{u.role?.replace("_", " ")}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {memberSearch.length > 0 && availableUsers.length === 0 && (
                      <div className="absolute z-50 top-full mt-1 left-0 w-full bg-white rounded-lg shadow-lg border border-slate-200 px-3 py-2">
                        <p className="text-sm text-slate-400">{language === "sv" ? "Inga matchande användare" : "No matching users"}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                    {language === "sv" ? "Avbryt" : "Cancel"}
                  </Button>
                  <Button type="submit" className="flex-1" data-testid="project-submit-btn">
                    {language === "sv" ? "Skapa" : "Create"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder={language === "sv" ? "Sök projekt..." : "Search projects..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12"
            data-testid="project-search-input"
          />
        </div>

        {/* Projects List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">
                {search 
                  ? (language === "sv" ? "Inga projekt hittades" : "No projects found")
                  : (language === "sv" ? "Inga projekt ännu. Skapa ditt första!" : "No projects yet. Create your first!")}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredProjects.map((project) => (
              <Link key={project.project_id} to={`/projects/${project.project_id}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`project-card-${project.project_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Barlow Condensed' }}>
                            {project.name}
                          </h3>
                          <Badge className={statusColors[project.status] || statusColors.active}>
                            {statusLabels[project.status] || project.status}
                          </Badge>
                        </div>
                        {project.project_number && (
                          <p className="text-sm text-slate-500 font-mono">{project.project_number}</p>
                        )}
                        {project.description && (
                          <p className="text-sm text-slate-600 mt-2 line-clamp-2">{project.description}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-500">
                          {project.address && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {project.address}
                            </span>
                          )}
                          {project.start_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {project.start_date}
                            </span>
                          )}
                          {project.budget > 0 && (
                            <span className="font-medium text-slate-700">
                              {project.budget.toLocaleString('sv-SE')} SEK
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 ml-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};
